import { DataGrid } from "@msuite/ui/data-grid";
import type { useFloating, useInteractions } from "@msuite/ui/libs/floating";
import { useTranslation } from "@msuite/ui/localization";
import { Portal } from "@msuite/ui/portal";
import {
	getAbsenceCategoryTranslationKey,
	getAbsenceStateTranslationKey,
} from "@msuite/utils/absences";
import { moment } from "@msuite/utils/date";
import type { AbsenceListItem } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import type { CSSProperties } from "react";
import { UserCard } from "@/components/cards";

/** Props Interface */
interface AbsencesCalendarBlockPopoverProps {
	absence: Omit<AbsenceListItem, "consumedDays">;
	floatingStyles: CSSProperties;
	setFloating: ReturnType<typeof useFloating>["refs"]["setFloating"];
	getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
}

export function AbsencesCalendarBlockPopover({
	absence,
	floatingStyles,
	getFloatingProps,
	setFloating,
}: AbsencesCalendarBlockPopoverProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<Portal>
			<div
				className="card"
				ref={setFloating}
				style={floatingStyles}
				{...getFloatingProps()}
			>
				<DataGrid>
					<DataGrid.Row>
						<DataGrid.Label>{t("general_fields.created_at")}</DataGrid.Label>
						<DataGrid.Value>{moment(absence.createdAt).longFormat()}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("general_fields.created_at")}</DataGrid.Label>
						<UserCard
							variant="plain"
							userId={absence.createdBy}
							withAvatar={false}
							render={(user) => <DataGrid.Value>{getFullName(user)}</DataGrid.Value>}
						/>
					</DataGrid.Row>
					<DataGrid.Divider />
					<DataGrid.Row>
						<DataGrid.Label>{t("dates.start_date")}</DataGrid.Label>
						<DataGrid.Value>{moment(absence.startDate).dateFormat()}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("dates.end_date")}</DataGrid.Label>
						<DataGrid.Value>{moment(absence.endDate).dateFormat()}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Divider />
					<DataGrid.Row>
						<DataGrid.Label>{t("absences.category.category")}</DataGrid.Label>
						<DataGrid.Value>{t(getAbsenceCategoryTranslationKey(absence.category))}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("general.state")}</DataGrid.Label>
						<DataGrid.Value>{t(getAbsenceStateTranslationKey(absence.state))}</DataGrid.Value>
					</DataGrid.Row>
				</DataGrid>
			</div>
		</Portal>
	);
}
